import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import ReportService from '../../services/ReportService';
import FinancialAccountService from '../../services/FinancialAccountService';

const currency = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });
const fmtMoney = (v) => currency.format(v ?? 0);
const fmtDate = (isoOrDate) => (isoOrDate ? new Date(isoOrDate).toLocaleDateString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '');

const toLocalYMD = (d) => {
    if (!(d instanceof Date)) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const accountTypesOptions = [
    { label: "Kasa", value: 1 },
    { label: "Banka", value: 2 },
    { label: "Kredi Kartı", value: 3 },
    { label: "POS", value: 4 },
    { label: "Sanal POS", value: 5 }
];

const getAccountTypeLabel = (type) => {
    switch (type) {
        case 1: return "Kasa";
        case 2: return "Banka";
        case 3: return "Kredi Kartı";
        case 4: return "POS";
        case 5: return "Sanal POS";
        default: return "Bilinmeyen";
    }
};

export default function AccountTransactionsReportPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    const [data, setData] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()]);

    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);

    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const toast = useRef(null);
    const dt = useRef(null);

    // Hesapları Yükle
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const res = await FinancialAccountService.getAll();
                const list = Array.isArray(res) ? res : (res?.data || []);
                setAccounts(list);
            } catch (err) {
                console.error("Hesaplar yüklenemedi", err);
            }
        };
        fetchAccounts();
    }, []);

    const fetchReport = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;
        setLoading(true);
        try {
            const res = await ReportService.getAccountTransactionsReport(
                toLocalYMD(dateRange[0]),
                toLocalYMD(dateRange[1]),
                selectedTypes,
                selectedAccounts
            );
            const reportData = res?.data?.data || [];
            setData(reportData);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Rapor alınamadı' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    // Seçilen tiplere göre hesap listesini filtrele
    const filteredAccounts = accounts.filter(acc => {
        if (selectedTypes.length === 0) return true;
        return selectedTypes.includes(acc.accountType);
    });

    const totals = {
        inbound: data.reduce((acc, r) => acc + (r.transactionType === "INBOUND" ? (r.amount || 0) : 0), 0),
        outbound: data.reduce((acc, r) => acc + (r.transactionType === "OUTBOUND" ? (r.amount || 0) : 0), 0)
    };
    totals.net = totals.inbound - totals.outbound;

    const header = (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 py-1 px-4">
            <span className="text-sm font-black text-slate-700 uppercase tracking-wider">
                İşlem Listesi
            </span>
            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                <span className="p-input-icon-left flex-1 sm:flex-none">
                    <i className="pi pi-search text-slate-400 pl-2" />
                    <InputText
                        value={globalFilterValue}
                        onChange={onGlobalFilterChange}
                        placeholder="Açıklama veya hesap ara..."
                        className="w-full sm:w-64 p-5 !py-2 bg-slate-50 border-slate-200 rounded-xl font-bold text-sm h-[38px]"
                    />
                </span>
                <Button
                    type="button"
                    icon="pi pi-file-excel"
                    label="EXCEL"
                    severity="success"
                    onClick={exportCSV}
                    className="!bg-emerald-600 border-none rounded-xl px-4 h-[38px] font-black text-[11px] text-white hover:!bg-emerald-700 transition-colors shadow-md shadow-emerald-600/10"
                />
            </div>
        </div>
    );

    return (
        <div className="p-2 md:p-4 bg-[#f8fafc] min-h-screen animate-fade-in">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

            {/* Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2rem] !bg-indigo-600 flex items-center justify-center shadow-2xl shadow-indigo-200">
                        <i className="pi pi-credit-card text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tighter uppercase">Hesap Hareketleri</h1>
                        <div className="flex items-center gap-2 ">
                            <span className="w-2 h-2 rounded-full !bg-indigo-500 animate-pulse" />
                            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest italic">Kasa, Banka ve POS İşlem Dökümü</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {[
                    { label: "Toplam Giriş", value: totals.inbound, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100", icon: "pi-plus-circle", shadow: "shadow-emerald-500/5" },
                    { label: "Toplam Çıkış", value: totals.outbound, color: "text-rose-600", bg: "bg-rose-50 border-rose-100", icon: "pi-minus-circle", shadow: "shadow-rose-500/5" },
                    { label: "Net Değişim", value: totals.net, color: totals.net >= 0 ? "!text-indigo-600" : "text-amber-600", bg: totals.net >= 0 ? "!bg-indigo-50 border-indigo-100" : "!bg-amber-50 border-amber-100", icon: "pi-wallet", shadow: "!shadow-indigo-500/5" }
                ].map((s, i) => (
                    <div key={i} className={`${s.bg} p-4 rounded-[2.5rem] border shadow-xl ${s.shadow} flex items-center justify-between transition-all hover:scale-[1.02]`}>
                        <div>
                            <p className={`text-[12px] font-black ${s.color} uppercase tracking-[0.15em] mb-2`}>{s.label}</p>
                            <h3 className={`text-2xl md:text-3xl font-black ${s.color} tracking-tighter`}>{fmtMoney(s.value)}</h3>
                        </div>
                        <div className={`w-3 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center ${s.color}`}>
                            <i className={`pi ${s.icon} text-xl`} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Modern Filter Card */}
            <div className="bg-white shadow-xl shadow-slate-100/60 border border-slate-200/80 rounded-[2.5rem] p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">
                            Tarih Aralığı
                        </span>
                        <Calendar
                            value={dateRange}
                            onChange={(e) => setDateRange(e.value)}
                            selectionMode="range"
                            readOnlyInput
                            dateFormat="dd.mm.yy"
                            className="w-full"
                            showIcon
                            inputClassName="font-bold text-slate-700 py-2 bg-slate-50 border-slate-200 rounded-xl text-sm"
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">
                            Hesap Türü
                        </span>
                        <MultiSelect
                            value={selectedTypes}
                            options={accountTypesOptions}
                            onChange={(e) => {
                                setSelectedTypes(e.value);
                                setSelectedAccounts([]);
                            }}
                            placeholder="Tümü"
                            maxSelectedLabels={1}
                            className="w-full font-bold text-sm bg-slate-50 border-slate-200 rounded-xl"
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">
                            Hesap Seçimi
                        </span>
                        <MultiSelect
                            value={selectedAccounts}
                            options={filteredAccounts}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => setSelectedAccounts(e.value)}
                            placeholder="Tümü"
                            maxSelectedLabels={1}
                            className="w-full font-bold text-sm bg-slate-50 border-slate-200 rounded-xl"
                        />
                    </div>

                    <Button
                        icon="pi pi-search"
                        label="RAPORU SORGULA"
                        onClick={fetchReport}
                        loading={loading}
                        className="w-full h-[38px] !bg-indigo-600 border-none hover:!bg-indigo-700 rounded-xl font-black text-[11px] shadow-lg shadow-indigo-600/20 text-white tracking-wider transition-all"
                    />
                </div>
            </div>

            <div className="bg-white shadow-2xl shadow-slate-200/50 border border-slate-200 rounded-[2.5rem] overflow-hidden">
                <div className="p-2 md:p-4">
                    <DataTable
                        ref={dt}
                        value={data}
                        loading={loading}
                        filters={filters}
                        globalFilterFields={['financialAccountName', 'description']}
                        header={header}
                        paginator
                        rows={15}
                        rowsPerPageOptions={[15, 30, 50]}
                        className="p-datatable-sm"
                        emptyMessage="Kayıt bulunamadı."
                        removableSort
                        rowClassName={() => "hover:bg-slate-50/80 transition-colors"}
                    >
                        <Column field="transactionDate" header="TARİH" sortable body={(r) => (
                            <div className="flex flex-col">
                                <span className="font-black text-slate-700">{fmtDate(r.transactionDate)}</span>
                            </div>
                        )} />

                        <Column field="financialAccountName" header="HESAP ADI" sortable body={(r) => (
                            <span className="font-bold text-slate-700">{r.financialAccountName}</span>
                        )} />

                        <Column field="accountType" header="HESAP TÜRÜ" body={(r) => (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-slate-100 text-slate-600">
                                {getAccountTypeLabel(r.accountType)}
                            </span>
                        )} />

                        <Column field="transactionType" header="İŞLEM TÜRÜ" body={(r) => (
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${r.transactionType === "INBOUND"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-rose-100 text-rose-700"
                                }`}>
                                {r.transactionType === "INBOUND" ? "GİRİŞ" : "ÇIKIŞ"}
                            </span>
                        )} />

                        <Column field="amount" header="TUTAR" sortable body={(r) => (
                            <span className={`font-bold ${r.transactionType === "INBOUND" ? "text-emerald-600" : "text-rose-600"}`}>
                                {r.transactionType === "INBOUND" ? "+" : "-"}{fmtMoney(r.amount)}
                            </span>
                        )} />

                        <Column field="description" header="AÇIKLAMA" body={(r) => (
                            <span className="text-slate-500 font-medium text-sm">{r.description || "-"}</span>
                        )} />
                    </DataTable>
                </div>
            </div>
        </div>
    );
}
